import { Checkbox, Col, Input, Row, Select, Spin } from "antd";
import { Formik } from "formik";
import React, { useCallback, useRef, useState } from "react";
import * as Yup from "yup";
import NumberFormat from "react-number-format";
import { useSelector } from "react-redux";
import ButtonStandard from "../../../utils/button";
import { Container, Footer, TableRestrictions } from "./styles";

const INITIAL_FORM_STATE = {
  reproductiveStatusList: [],
  minSupplementOfferedPerDay: null,
  maxSupplementOfferedPerDay: null,
  supplementOfferedUnitType: "PV_ANIMAL",
  maxDays: null,
  minMaleAgeMonths: null,
  maxMaleAgeMonths: null,
  minFemaleAgeMonths: null,
  maxFemaleAgeMonths: null,
  minCalfAgeMonths: null,
  maxCalfAgeMonths: null,
  minHeiferAgeMonths: null,
  maxHeiferAgeMonths: null,
  minMaleLiveWeight: null,
  maxMaleLiveWeight: null,
  minFemaleLiveWeight: null,
  maxFemaleLiveWeight: null,
  minCalfLiveWeight: null,
  maxCalfLiveWeight: null,
  minHeiferLiveWeight: null,
  maxHeiferLiveWeight: null,
  status: "NI",
};

const REPRODUCTIVE_STATUS_OPTIONS = [
  "castrated_male", // macho castrado
  "pregnant_female", // femea gestando
  "anestrous_female", // femea anestro
  "lactating_female", // femea lactante
  "lactating_calf", // bez/nov lactante
  "supplemented_animals", // animais suplementados
  "breeding_males", // machos reprodutores
  "dairy_cows", // vacas leiteiras
  "animals_on_pasture", // animais a pasto
];

const validationSchema = Yup.object().shape({
  maxSupplementOfferedPerDay: Yup.number().nullable(),
  minSupplementOfferedPerDay: Yup.number()
    .nullable()
    .when(
      ["maxSupplementOfferedPerDay"],
      (maxSupplementOfferedPerDay, schema) => {
        return !!maxSupplementOfferedPerDay
          ? schema.lessThan(
              maxSupplementOfferedPerDay,
              "Should be less than max"
            )
          : schema;
      }
    ),
  maxMaleAgeMonths: Yup.number().nullable(),
  minMaleAgeMonths: Yup.number()
    .nullable()
    .when(["maxMaleAgeMonths"], (maxMaleAgeMonths, schema) => {
      return !!maxMaleAgeMonths
        ? schema.lessThan(maxMaleAgeMonths, "Should be less than max")
        : schema;
    }),
  maxFemaleAgeMonths: Yup.number().nullable(),
  minFemaleAgeMonths: Yup.number()
    .nullable()
    .when(["maxFemaleAgeMonths"], (maxFemaleAgeMonths, schema) => {
      return !!maxFemaleAgeMonths
        ? schema.lessThan(maxFemaleAgeMonths, "Should be less than max")
        : schema;
    }),
  maxCalfAgeMonths: Yup.number().nullable(),
  minCalfAgeMonths: Yup.number()
    .nullable()
    .when(["maxCalfAgeMonths"], (maxCalfAgeMonths, schema) => {
      return !!maxCalfAgeMonths
        ? schema.lessThan(maxCalfAgeMonths, "Should be less than max")
        : schema;
    }),
  maxHeiferAgeMonths: Yup.number().nullable(),
  minHeiferAgeMonths: Yup.number()
    .nullable()
    .when(["maxHeiferAgeMonths"], (maxHeiferAgeMonths, schema) => {
      return !!maxHeiferAgeMonths
        ? schema.lessThan(maxHeiferAgeMonths, "Should be less than max")
        : schema;
    }),
  maxMaleLiveWeight: Yup.number().nullable(),
  minMaleLiveWeight: Yup.number()
    .nullable()
    .when(["maxMaleLiveWeight"], (maxMaleLiveWeight, schema) => {
      return !!maxMaleLiveWeight
        ? schema.lessThan(maxMaleLiveWeight, "Should be less than max")
        : schema;
    }),
  maxFemaleLiveWeight: Yup.number().nullable(),
  minFemaleLiveWeight: Yup.number()
    .nullable()
    .when(["maxFemaleLiveWeight"], (maxFemaleLiveWeight, schema) => {
      return !!maxFemaleLiveWeight
        ? schema.lessThan(maxFemaleLiveWeight, "Should be less than max")
        : schema;
    }),
  maxCalfLiveWeight: Yup.number().nullable(),
  minCalfLiveWeight: Yup.number()
    .nullable()
    .when(["maxCalfLiveWeight"], (maxCalfLiveWeight, schema) => {
      return !!maxCalfLiveWeight
        ? schema.lessThan(maxCalfLiveWeight, "Should be less than max")
        : schema;
    }),
  maxHeiferLiveWeight: Yup.number().nullable(),
  minHeiferLiveWeight: Yup.number()
    .nullable()
    .when(["maxHeiferLiveWeight"], (maxHeiferLiveWeight, schema) => {
      return !!maxHeiferLiveWeight
        ? schema.lessThan(maxHeiferLiveWeight, "Should be less than max")
        : schema;
    }),
});

const DrawerSupplementRestrictions = ({
  data,
  onSave,
  onCancel,
  drawerVisible,
}) => {
  const formikRef = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const { translation } = useSelector((state) => state.app);

  const supplementOfferedUnitType = useCallback(
    (field, formValue, setFieldValue) => {
      return (
        <Select
          value={formValue}
          style={{ width: 100 }}
          onChange={(value) => setFieldValue(field, value)}
        >
          <Select.Option value="PV_ANIMAL">%PV</Select.Option>
          <Select.Option value="I_MS_PV_ANIMAL">%IMS PV</Select.Option>
          <Select.Option value="G_KG_PV_ANIMAL">g/kg de PV</Select.Option>
          <Select.Option value="G_ANIMAL">g/dia</Select.Option>
          <Select.Option value="KG_ANIMAL">kg/dia</Select.Option>
        </Select>
      );
    },
    []
  );

  function onSubmit(values, actions) {
    const data = values;
    data.status = "I";
    onSave(data);
  }

  function handleOpenDrawer(visible) {
    if (visible) {
      if (data === null || data === undefined) {
        formikRef.current.resetForm(INITIAL_FORM_STATE);
      }
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }

  return (
    <Container
      title={translation.supplement.formFarmRestrictions.title}
      width={600}
      onClose={onCancel}
      maskClosable={false}
      visible={drawerVisible}
      afterVisibleChange={handleOpenDrawer}
    >
      <Formik
        ref={formikRef}
        initialValues={data || INITIAL_FORM_STATE}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            {/* Body */}
            <div className="drawerForm">
              <Spin spinning={isLoading}>
                {Object.entries(errors).length > 0 &&
                submitCount > 0 &&
                Object.entries(errors).filter(
                  ([key, value]) => value === "Should be less than max"
                ).length > 0 ? (
                  <Row type="flex" justify="center" align="middle">
                    <label className="error">Veja os valores minimos</label>
                  </Row>
                ) : (
                  Object.entries(errors).length > 0 &&
                  submitCount > 0 && (
                    <Row type="flex" justify="center" align="middle">
                      <label className="error">
                        {translation.error.formError}
                      </label>
                    </Row>
                  )
                )}
                <Row type="flex" gutter={8}>
                  <Col span={12}>
                    <Row>
                      <label>
                        {
                          translation.supplement.formFarmRestrictions
                            .minSupplementOfferedPerDay
                        }
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={values.minSupplementOfferedPerDay}
                        name="minSupplementOfferedPerDay"
                        addonAfter={supplementOfferedUnitType(
                          "supplementOfferedUnitType",
                          values.supplementOfferedUnitType,
                          setFieldValue
                        )}
                        decimalScale={4}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          setFieldValue(
                            "minSupplementOfferedPerDay",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                              ? values.voluntaryConsumptionType === "PV_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : values.voluntaryConsumptionType ===
                                  "I_MS_PV_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : values.voluntaryConsumptionType ===
                                  "KG_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : floatValue > 10000
                                ? 10000
                                : floatValue
                              : 0
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col span={12}>
                    <Row>
                      <label>
                        {
                          translation.supplement.formFarmRestrictions
                            .maxSupplementOfferedPerDay
                        }
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={values.maxSupplementOfferedPerDay}
                        name="maxSupplementOfferedPerDay"
                        addonAfter={supplementOfferedUnitType(
                          "supplementOfferedUnitType",
                          values.supplementOfferedUnitType,
                          setFieldValue
                        )}
                        decimalScale={4}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          setFieldValue(
                            "maxSupplementOfferedPerDay",
                            floatValue == null
                              ? null
                              : floatValue >= 0
                              ? values.voluntaryConsumptionType === "PV_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : values.voluntaryConsumptionType ===
                                  "I_MS_PV_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : values.voluntaryConsumptionType ===
                                  "KG_ANIMAL"
                                ? floatValue > 100
                                  ? 100
                                  : floatValue
                                : floatValue > 10000
                                ? 10000
                                : floatValue
                              : 0
                          );
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row type="flex" gutter={8}>
                  <Col span={8}>
                    <Row>
                      <label>
                        {translation.supplement.formFarmRestrictions.maxDays}
                      </label>
                    </Row>
                    <Row>
                      <NumberFormat
                        customInput={Input}
                        placeholder={translation.defaultPlaceholder}
                        value={values.maxDays}
                        name="maxDays"
                        decimalScale={0}
                        decimalSeparator=","
                        thousandSeparator="."
                        fixedDecimalScale={true}
                        onValueChange={({ floatValue }) => {
                          setFieldValue("maxDays", floatValue);
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
                <Row type="flex" gutter={8}>
                  <TableRestrictions>
                    <thead>
                      <tr>
                        <th rowSpan="2"></th>
                        <th colSpan="2">
                          {translation.supplement.formFarmRestrictions.age}
                        </th>
                        <th colSpan="2">
                          {
                            translation.supplement.formFarmRestrictions
                              .liveWeight
                          }
                        </th>
                      </tr>
                      <tr>
                        <th>
                          {translation.supplement.formFarmRestrictions.minimum}
                        </th>
                        <th>
                          {translation.supplement.formFarmRestrictions.maximum}
                        </th>
                        <th>
                          {translation.supplement.formFarmRestrictions.minimum}
                        </th>
                        <th>
                          {translation.supplement.formFarmRestrictions.maximum}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {translation.supplement.formFarmRestrictions.male}
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minMaleAgeMonths}
                            name="minMaleAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minMaleAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxMaleAgeMonths}
                            name="maxMaleAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxMaleAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minMaleLiveWeight}
                            name="minMaleLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minMaleLiveWeight", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxMaleLiveWeight}
                            name="maxMaleLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxMaleLiveWeight", floatValue);
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {translation.supplement.formFarmRestrictions.female}
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minFemaleAgeMonths}
                            name="minFemaleAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minFemaleAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxFemaleAgeMonths}
                            name="maxFemaleAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxFemaleAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minFemaleLiveWeight}
                            name="minFemaleLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minFemaleLiveWeight", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxFemaleLiveWeight}
                            name="maxFemaleLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxFemaleLiveWeight", floatValue);
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {translation.supplement.formFarmRestrictions.calf}
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minCalfAgeMonths}
                            name="minCalfAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minCalfAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxCalfAgeMonths}
                            name="maxCalfAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxCalfAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minCalfLiveWeight}
                            name="minCalfLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minCalfLiveWeight", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxCalfLiveWeight}
                            name="maxCalfLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxCalfLiveWeight", floatValue);
                            }}
                          />
                        </td>
                      </tr>
                      <tr>
                        <td>
                          {translation.supplement.formFarmRestrictions.heifer}
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minHeiferAgeMonths}
                            name="minHeiferAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minHeiferAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxHeiferAgeMonths}
                            name="maxHeiferAgeMonths"
                            decimalScale={0}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxHeiferAgeMonths", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.minHeiferLiveWeight}
                            name="minHeiferLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("minHeiferLiveWeight", floatValue);
                            }}
                          />
                        </td>
                        <td>
                          <NumberFormat
                            customInput={Input}
                            placeholder={translation.defaultPlaceholder}
                            value={values.maxHeiferLiveWeight}
                            name="maxHeiferLiveWeight"
                            decimalScale={2}
                            decimalSeparator=","
                            thousandSeparator="."
                            fixedDecimalScale={true}
                            onValueChange={({ floatValue }) => {
                              setFieldValue("maxHeiferLiveWeight", floatValue);
                            }}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </TableRestrictions>
                </Row>
                <Row type="flex" gutter={8}>
                  <Row>
                    <label>
                      {
                        translation.supplement.formFarmRestrictions
                          .reproductiveStatusList
                      }
                    </label>
                  </Row>
                </Row>
                <Row type="flex" gutter={8}>
                  {REPRODUCTIVE_STATUS_OPTIONS.map((option) => (
                    <Col key={option} span={12} style={{ padding: 4 }}>
                      <Checkbox
                        checked={values.reproductiveStatusList.includes(option)}
                        onChange={(e) => {
                          const isCheck = e.target.checked;
                          if (isCheck) {
                            setFieldValue("reproductiveStatusList", [
                              ...values.reproductiveStatusList,
                              option,
                            ]);
                          } else {
                            setFieldValue(
                              "reproductiveStatusList",
                              values.reproductiveStatusList.filter(
                                (status) => status !== option
                              )
                            );
                          }
                        }}
                      >
                        {
                          translation.supplement.formFarmRestrictions
                            .reproductiveStatusOptions[option]
                        }
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              </Spin>
            </div>
            {/* Footer */}
            <Footer>
              <Row type="flex" justify="space-between">
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="selectDiv"
                ></Col>
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  xl={12}
                  className="buttonsDiv"
                >
                  <ButtonStandard
                    type="button"
                    buttonType="type7"
                    onClick={onCancel}
                  >
                    {translation.buttons.cancel}
                  </ButtonStandard>

                  <ButtonStandard type="submit" buttonType="type6">
                    {translation.buttons.save}
                  </ButtonStandard>
                </Col>
              </Row>
            </Footer>
          </form>
        )}
      />
    </Container>
  );
};

export default DrawerSupplementRestrictions;
